export interface BotSkillProfileInput {
  versusElo?: number | null;
  level?: number | null;
  versusPlayed?: number | null;
  momentum?: number | null;
}

export type BotSkillSegment = "novice" | "midcore" | "experienced";

export interface BotFairnessEnvelope {
  pressure: number;
  consistency: number;
  mistakeAllowance: number;
}

export interface BotAntiSnowballProfile {
  leadStartScoreDiff: number;
  pressureReductionMax: number;
  neutralWeightBoostMax: number;
  mistakeWeightBoostMax: number;
  easingStrength: number;
}

export interface BotSkillProfile {
  segment: BotSkillSegment;
  momentum: number;
  fairness: BotFairnessEnvelope;
  antiSnowball: BotAntiSnowballProfile;
}

const DEFAULT_ELO = 1000;
const DEFAULT_LEVEL = 1;
const DEFAULT_PLAYED = 0;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function lerp(min: number, max: number, t: number): number {
  return min + (max - min) * t;
}

function asFiniteOrDefault(value: number | null | undefined, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  return value;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function classifySegment(experience01: number): BotSkillSegment {
  if (experience01 < 0.33) {
    return "novice";
  }
  if (experience01 < 0.66) {
    return "midcore";
  }
  return "experienced";
}

export function createBotSkillProfile(input: BotSkillProfileInput): BotSkillProfile {
  const versusElo = clamp(asFiniteOrDefault(input.versusElo, DEFAULT_ELO), 600, 2400);
  const level = clamp(asFiniteOrDefault(input.level, DEFAULT_LEVEL), 1, 100);
  const versusPlayed = clamp(asFiniteOrDefault(input.versusPlayed, DEFAULT_PLAYED), 0, 5000);
  const momentum = clamp(asFiniteOrDefault(input.momentum, 0), -1, 1);

  const elo01 = clamp((versusElo - 850) / 600, 0, 1);
  const level01 = clamp((level - 1) / 99, 0, 1);
  const played01 = clamp(Math.sqrt(versusPlayed / 200), 0, 1);

  const experienceBase = (elo01 * 0.5) + (level01 * 0.25) + (played01 * 0.25);
  const experience01 = clamp(experienceBase + (momentum * 0.08), 0, 1);
  const segment = classifySegment(experience01);

  let pressure = lerp(0.32, 0.68, experience01);
  let consistency = lerp(0.4, 0.8, experience01);
  let mistakeAllowance = lerp(0.42, 0.14, experience01);

  if (segment === "novice") {
    pressure = Math.min(pressure, 0.46);
    consistency = Math.min(consistency, 0.58);
    mistakeAllowance = Math.max(mistakeAllowance, 0.28);
  }

  if (segment === "experienced") {
    pressure = Math.max(pressure, 0.58);
    consistency = Math.max(consistency, 0.68);
    mistakeAllowance = Math.min(mistakeAllowance, 0.22);
  }

  pressure = clamp(pressure, 0.3, 0.72);
  consistency = clamp(consistency, 0.38, 0.84);
  mistakeAllowance = clamp(mistakeAllowance, 0.1, 0.46);

  const antiSnowball: BotAntiSnowballProfile = {
    leadStartScoreDiff: Math.round(lerp(700, 1150, experience01)),
    pressureReductionMax: round3(clamp(lerp(0.28, 0.14, experience01), 0.1, 0.32)),
    neutralWeightBoostMax: round3(clamp(lerp(0.26, 0.12, experience01), 0.08, 0.28)),
    mistakeWeightBoostMax: round3(clamp(lerp(0.22, 0.08, experience01), 0.06, 0.24)),
    easingStrength: round3(clamp(lerp(0.85, 0.45, experience01), 0.4, 0.9))
  };

  return {
    segment,
    momentum: round3(momentum),
    fairness: {
      pressure: round3(pressure),
      consistency: round3(consistency),
      mistakeAllowance: round3(mistakeAllowance)
    },
    antiSnowball
  };
}
